import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Processing...");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        console.log("AuthCallback: Starting authentication process");
        console.log("AuthCallback: Current URL:", window.location.href);
        console.log("AuthCallback: Hash:", window.location.hash);
        
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error("AuthCallback: No session_id found in URL hash");
          setError("No session ID found. Please try again.");
          setTimeout(() => navigate("/login", { replace: true }), 2000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log("AuthCallback: Found session_id:", sessionId.substring(0, 10) + "...");
        setStatus("Verifying with Google...");

        // Call our backend to exchange session_id (avoids CORS issues)
        // Backend will call Emergent Auth server-side
        console.log("AuthCallback: Sending session_id to backend...");
        console.log("AuthCallback: API URL:", API);
        console.log("AuthCallback: Full endpoint:", `${API}/auth/google/session`);
        
        const response = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId },
          { 
            withCredentials: true,
            timeout: 30000 // 30 second timeout
          }
        );

        console.log("AuthCallback: Backend response received");
        const { token, user } = response.data;

        // Store JWT token for our existing auth system
        localStorage.setItem("expertbase_token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        console.log("AuthCallback: Token stored in localStorage");

        // Fetch user data
        setStatus("Finalizing login...");
        await fetchUser();
        console.log("AuthCallback: User data fetched, user is_expert:", user?.is_expert);
        
        // Redirect based on user type
        // Experts → Expert Dashboard
        // Regular users → Homepage (to browse experts)
        const redirectPath = user?.is_expert ? "/expert-dashboard" : "/";
        console.log("AuthCallback: Redirecting to:", redirectPath);
        
        window.history.replaceState(null, "", redirectPath);
        navigate(redirectPath, { replace: true });
      } catch (error) {
        console.error("AuthCallback: Error during authentication:", error);
        console.error("AuthCallback: Error name:", error.name);
        console.error("AuthCallback: Error message:", error.message);
        console.error("AuthCallback: Error response:", error.response?.data);
        console.error("AuthCallback: Error request URL:", error.config?.url);
        
        // More specific error messages
        let errorMessage = "Authentication failed. Please try again.";
        if (error.message === "Network Error") {
          errorMessage = "Network error - please check your connection and try again.";
          console.error("AuthCallback: Network Error - possible causes: CORS, backend unreachable, or network issue");
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.status === 401) {
          errorMessage = "Session expired. Please try signing in again.";
        }
        
        setError(errorMessage);
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      }
    };

    processAuth();
  }, [navigate, fetchUser]);

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <p className="text-red-400 mb-2">{error}</p>
            <p className="text-slate-500 text-sm">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400">{status}</p>
          </>
        )}
      </div>
    </div>
  );
}
